import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
    VideoCameraIcon,
    MicrophoneIcon,
    ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { getNameInitials } from '../../../common/nameInitials';
import { Button } from '../../../components/buttons/Button';
import { SearchableSelect } from '../../../components/searchableSelect/SearchableSelect';
import { InputSwitch } from '../../../components/inputSwitch/InputSwitch';
import { usePreJoinMedia } from '../../../hooks/usePreJoinMedia';
import { setPreJoinDevicePreferences } from '../../../utils/preJoinMediaPreferences';
import './zoom-join-prompt.css';

const deviceIconClass = 'h-5 w-5 text-slate-500';

const deviceLabel = (device, index, fallback) => (
    device.label || `${fallback} ${index + 1}`
);

export const ZoomJoinConnecting = () => (
    <div className="zoom-join-prompt__connecting" role="status" aria-live="polite">
        <span className="zoom-join-prompt__spinner" aria-hidden="true" />
        <p>Conectando a la videollamada…</p>
    </div>
);

export const ZoomJoinPrompt = ({
    subtitle,
    loading,
    onConfirm,
    onCancel,
}) => {
    const { user } = useSelector((state) => state.user);
    const {
        status,
        permissionHint,
        microphones,
        cameras,
        selectedMicId,
        selectedCameraId,
        videoEnabled,
        audioEnabled,
        joinWithMic,
        joinWithCamera,
        videoRef,
        selectMicrophone,
        selectCamera,
        setJoinWithMic,
        setJoinWithCamera,
    } = usePreJoinMedia();

    const displayName = [user?.firstName, user?.lastName].filter(Boolean).join(' ')
        || user?.name
        || 'Participante';
    const initials = getNameInitials(displayName) || '?';
    const permissionsBlocked = permissionHint === 'denied' || status === 'denied';
    const devicesDisabled = loading || permissionsBlocked || status === 'loading';
    const showCameraPreview = videoEnabled && joinWithCamera;

    const microphoneOptions = useMemo(
        () => microphones.map((device, index) => ({
            value: device.deviceId,
            label: deviceLabel(device, index, 'Micrófono'),
            icon: <MicrophoneIcon className={deviceIconClass} aria-hidden="true" />,
        })),
        [microphones],
    );

    const cameraOptions = useMemo(
        () => cameras.map((device, index) => ({
            value: device.deviceId,
            label: deviceLabel(device, index, 'Cámara'),
            icon: <VideoCameraIcon className={deviceIconClass} aria-hidden="true" />,
        })),
        [cameras],
    );

    const handleConfirm = () => {
        setPreJoinDevicePreferences({
            micId: selectedMicId,
            cameraId: selectedCameraId,
            joinWithMic: joinWithMic && audioEnabled,
            joinWithCamera: joinWithCamera && videoEnabled,
        });
        onConfirm?.();
    };

    return (
        <section className="zoom-join-prompt" aria-labelledby="zoom-join-title">
            <header className="zoom-join-prompt__bar">
                <span className="zoom-join-prompt__live">
                    <span className="zoom-join-prompt__live-dot" aria-hidden="true" />
                    En vivo
                </span>
                <div className="zoom-join-prompt__meta">
                    <h2 id="zoom-join-title" className="zoom-join-prompt__title">
                        Unirse a la videollamada
                    </h2>
                    {subtitle ? (
                        <p className="zoom-join-prompt__subtitle">{subtitle}</p>
                    ) : null}
                </div>
            </header>

            <main className="zoom-join-prompt__stage">
                <div className="zoom-join-prompt__preview-wrap">
                    <div className="zoom-join-prompt__preview">
                        <video
                            ref={videoRef}
                            className={`zoom-join-prompt__video${showCameraPreview ? '' : ' is-hidden'}`}
                            autoPlay
                            muted
                            playsInline
                            aria-label={`Vista previa de ${displayName}`}
                        />
                        {!showCameraPreview ? (
                            <div className="zoom-join-prompt__avatar">{initials}</div>
                        ) : null}
                        <p className="zoom-join-prompt__name">{displayName}</p>
                    </div>

                    <div className="zoom-join-prompt__pickers">
                        <div className="zoom-join-prompt__device">
                            <span className="zoom-join-prompt__device-label">Micrófono</span>
                            <div className="zoom-join-prompt__device-row">
                                <div className="zoom-join-prompt__device-select">
                                    <SearchableSelect
                                        maxVisibleOptions={4}
                                        options={microphoneOptions}
                                        value={selectedMicId}
                                        onChange={selectMicrophone}
                                        placeholder={
                                            microphones.length === 0
                                                ? 'Sin micrófono detectado'
                                                : 'Seleccionar micrófono'
                                        }
                                        searchPlaceholder="Buscar micrófono..."
                                        emptyMessage="No se encontraron micrófonos"
                                        disabled={devicesDisabled || microphones.length === 0}
                                    />
                                </div>
                                <InputSwitch
                                    label="Activar micrófono al unirse"
                                    hideLabel
                                    enabled={Boolean(joinWithMic && microphones.length > 0)}
                                    setEnabled={setJoinWithMic}
                                    disabled={devicesDisabled || microphones.length === 0}
                                />
                            </div>
                        </div>

                        <div className="zoom-join-prompt__device">
                            <span className="zoom-join-prompt__device-label">Cámara</span>
                            <div className="zoom-join-prompt__device-row">
                                <div className="zoom-join-prompt__device-select">
                                    <SearchableSelect
                                        maxVisibleOptions={4}
                                        options={cameraOptions}
                                        value={selectedCameraId}
                                        onChange={selectCamera}
                                        placeholder={
                                            cameras.length === 0
                                                ? 'Sin cámara detectada'
                                                : 'Seleccionar cámara'
                                        }
                                        searchPlaceholder="Buscar cámara..."
                                        emptyMessage="No se encontraron cámaras"
                                        disabled={devicesDisabled || cameras.length === 0}
                                    />
                                </div>
                                <InputSwitch
                                    label="Activar cámara al unirse"
                                    hideLabel
                                    enabled={Boolean(joinWithCamera && cameras.length > 0)}
                                    setEnabled={setJoinWithCamera}
                                    disabled={devicesDisabled || cameras.length === 0}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </main>

            <footer className="zoom-join-prompt__dock">
                {permissionsBlocked ? (
                    <p className="zoom-join-prompt__alert" role="alert">
                        <ExclamationTriangleIcon
                            className="inline w-4 h-4 mr-1 align-text-bottom"
                            aria-hidden="true"
                        />
                        Los permisos están bloqueados. Habilítalos en el candado de la barra
                        de direcciones e intenta de nuevo.
                    </p>
                ) : status === 'loading' ? (
                    <p className="zoom-join-prompt__hint">Preparando cámara y micrófono…</p>
                ) : !audioEnabled && !videoEnabled ? (
                    <p className="zoom-join-prompt__hint">
                        No se detectó cámara ni micrófono. Podrás unirte en modo escucha.
                    </p>
                ) : !videoEnabled ? (
                    <p className="zoom-join-prompt__hint">
                        Sin cámara detectada.
                        {joinWithMic && audioEnabled
                            ? ' Te unirás solo con audio.'
                            : ' Entrarás con el micrófono desactivado.'}
                    </p>
                ) : !audioEnabled ? (
                    <p className="zoom-join-prompt__hint">
                        Sin micrófono detectado. Podrás ver la reunión sin hablar.
                    </p>
                ) : !joinWithMic && !joinWithCamera ? (
                    <p className="zoom-join-prompt__hint">
                        Entrarás con el micrófono y la cámara desactivados.
                    </p>
                ) : !joinWithMic ? (
                    <p className="zoom-join-prompt__hint">
                        Entrarás con el micrófono desactivado.
                    </p>
                ) : !joinWithCamera ? (
                    <p className="zoom-join-prompt__hint">
                        Entrarás con la cámara desactivada.
                    </p>
                ) : null}

                <div className="zoom-join-prompt__actions">
                    {onCancel ? (
                        <Button
                            type="button"
                            variant="secondary"
                            size="md"
                            disabled={loading}
                            onClick={onCancel}
                        >
                            Cancelar
                        </Button>
                    ) : null}
                    <Button
                        type="button"
                        variant="primary"
                        size="md"
                        disabled={loading || permissionsBlocked || status === 'loading'}
                        loading={loading}
                        icon={<VideoCameraIcon className="w-4 h-4" aria-hidden="true" />}
                        onClick={handleConfirm}
                    >
                        {loading ? 'Conectando…' : 'Unirse ahora'}
                    </Button>
                </div>
            </footer>
        </section>
    );
};
